import axios from 'axios';

/**
 * Single source of truth for "is this a transient/recoverable error?"
 *
 * Transient errors are network/server hiccups that should NOT clear auth state
 * or trigger a logout — the right response is to retry or wait, not to treat the
 * session as invalid. Authentication failures (401/403) and genuine application
 * bugs are NOT transient and must surface (report + clear state where relevant).
 *
 * Only Axios errors can be transient. We gate on `axios.isAxiosError` first, so a
 * non-Axios value (a plain `Error` thrown by application code, a bug in a callback)
 * returns `false` — it is a real failure that should be reported, not silently
 * retried. This is deliberate per project value 3 (fail visibly, never silently):
 * a blind `error as AxiosError` cast that treats any responseless error as
 * transient is itself a fail-silent pattern that masks real bugs.
 *
 * Timeouts and aborted requests (`ECONNABORTED`) arrive as Axios errors with no
 * `.response`, so they are covered by the no-response branch below.
 */
export function isTransientApiError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }

  // Network error / timeout / aborted request — no response was ever received.
  if (!error.response) {
    return true;
  }

  // Server errors that indicate temporary unavailability (e.g. deploy rollouts).
  const status = error.response.status;
  return status === 502 || status === 503 || status === 504;
}
